import { exists } from '@jack-henry/frontend-utils/functions';
import { TmFormControl } from './tm-form-control';
import { FormControlValidationResult } from './tm-form-control.types';
import { FormValidationBuilderCallback, FormValidationResult } from './tm-form.types';

/**
 * Given the controls in a form, build a validation result.
 *
 * @param builder A callback invoked against each form control.
 * @returns A `FormValidationResult` if there are validation errors, or `true` if all controls are valid.
 */
export function buildFormValidationResult<T>(
    controls: TmFormControl<unknown>[],
    builder: FormValidationBuilderCallback<T>
): FormValidationResult<T> | true {
    const result = controls
        .map(control => {
            const { value, name } = control;
            const errors = builder(name as keyof T, value as T[keyof T]);

            if (errors && errors.length > 0) {
                return new FormControlValidationResult(control).addErrors(...errors);
            }
            return undefined;
        })
        .filter(exists)
        .reduce((dict, result) => {
            if (!result.isValid) {
                dict[result.key as keyof T] = result as FormControlValidationResult<T[keyof T]>;
            }

            return dict;
        }, {} as FormValidationResult<T>);

    // if there's no errors in the result, don't return a digest
    return Object.keys(result).length === 0 ? true : result;
}
